// Full-screen, cinematic announcements of the big moments, so every player notices them:
// whose turn it is, the mythos phase, the ship surging ahead, new waypoints, ritual progress,
// awakening, damage, monarchs, traitors.
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import * as uf from "../../../../common/unfathomable";
import { art } from "./assets";
import { roundLabel } from "./Panels";
import { kSeatColors } from "./seatColors";
import css from "./Unfathomable.module.css";

type Tone = "sea" | "ritual" | "danger" | "dread" | "hope" | "turn" | "you" | "mythos" | "pass" | "fail";
interface Notice {
  key: number; icon: string; title: string; sub: string; tone: Tone;
  /** Turn notices show the player's portrait medallion. */
  player?: uf.PlayerView;
  hold?: number;
}

const kHold = 2300;
const kResourceIcon: Record<uf.Resource, string> = { fuel: "⛽", food: "🥫", sanity: "🧠", souls: "👥" };
/** Each queued notice gets its own id: the hold timer is keyed on it. */
let noticeIds = 0;

/** Why a title changed hands, said about its previous holder. */
const kTitleWhy: Record<Extract<uf.GameEvent, { type: "title" }>["why"], (from: string) => string> = {
  reveal: from => `${from} 公开叛徒身份，失去头衔`,
  brig: from => `${from} 被关进禁闭室，失去头衔`,
  defeat: from => `${from} 倒下，失去头衔`,
  card: from => `接替 ${from}`,
};

function noticesFor(view: uf.GameView, fresh: uf.EventEntry[], last: { travel: number; ritual: number }): Notice[] {
  const out: Notice[] = [];
  const name = (seat: number | undefined) => (seat === undefined ? "" : view.players[seat]?.name ?? "");
  // Resource losses in one update are one notice (at the first loss), with what is left.
  const losses = fresh.filter(e => e.ev.type === "resource" && e.ev.delta < 0);
  const lossTotals = new Map<uf.Resource, number>();
  for (const { ev } of losses) if (ev.type === "resource") lossTotals.set(ev.resource, (lossTotals.get(ev.resource) ?? 0) + ev.delta);
  for (const { seq, ev } of fresh) {
    switch (ev.type) {
      case "resource":
        if (losses.length > 0 && seq === losses[0].seq) {
          const parts = [...lossTotals].map(([r, d]) => `${kResourceIcon[r]} ${uf.kResourceNameZh[r]} ${d}（剩 ${view.resources[r]}）`);
          const low = [...lossTotals.keys()].some(r => view.resources[r] <= 2);
          out.push({ key: seq, icon: "📉", title: low ? "资源告急" : "资源损失", sub: parts.join("　"), tone: "danger", hold: 2000 });
        }
        break;
      case "check": {
        const name = ev.title ? `「${ev.title}」` : "";
        const score = ev.target !== undefined ? `${ev.total} / ${ev.target}` : `总值 ${ev.total}`;
        if (ev.result === "pass") out.push({ key: seq, icon: "✔", title: "检定通过", sub: `${name} ${score}`, tone: "pass", hold: 1800 });
        else if (ev.result === "partial") out.push({ key: seq, icon: "◐", title: "部分通过", sub: `${name} ${score}`, tone: "hope", hold: 1800 });
        else out.push({ key: seq, icon: "✘", title: "检定失败", sub: `${name} ${score}`, tone: "fail", hold: 1800 });
        break;
      }
      case "turn": {
        const p = view.players[ev.seat];
        if (!p) break;
        const mine = ev.seat === view.you;
        out.push({
          key: seq, icon: "", player: p, tone: mine ? "you" : "turn", hold: 1900,
          title: mine ? "轮到你了！" : `${p.name} 的回合`,
          sub: `${uf.kCharacters[p.character].name} · ${roundLabel(ev.turn, view.players.length)} · 行动阶段`,
        });
        break;
      }
      case "mythos": {
        const m = uf.kMythosById[ev.card];
        out.push({ key: seq, icon: "🜏", title: "神话阶段", sub: `「${m.name}」自深海浮现`, tone: "mythos", hold: 2000 });
        break;
      }
      case "track":
        if (ev.track === "travel" && ev.value > last.travel) {
          out.push({ key: seq, icon: "⚓", title: "全速前进", sub: ev.value >= 4 ? "亚特兰蒂卡号抵达航点！" : `锅炉轰鸣，船身甩开了追兵 · 航迹 ${ev.value}/4`, tone: "sea" });
        }
        if (ev.track === "ritual" && ev.value > last.ritual) {
          out.push({ key: seq, icon: "✶", title: "仪式推进", sub: `古老的音节在礼拜堂回荡 · 仪式轨 ${ev.value}/4`, tone: "ritual" });
        }
        last[ev.track] = ev.value;
        break;
      case "waypoint": {
        const w = uf.kWaypointById[ev.id];
        out.push({ key: seq, icon: "🧭", title: `驶向「${w.name}」`, sub: `${name(ev.by)} 选定航线 · 距离 ${w.distance} · 累计航程 ${view.distance}/12`, tone: "hope" });
        break;
      }
      case "banishment":
        out.push({ key: seq, icon: "✷", title: "大驱逐咒", sub: "咒文完成——海中的怪物被驱回深渊", tone: "ritual" });
        break;
      case "awakening":
        out.push({ key: seq, icon: "👁", title: "觉　醒", sub: "血脉在暗处苏醒……每名玩家再获得一张忠诚卡", tone: "dread" });
        break;
      case "damageDrawn": {
        const d = uf.kDamageById[ev.card];
        if (d.repair) break; // structural: announced by the "damage" event with its space
        const kNon: Record<string, { icon: string; sub: string }> = {
          fuelLeak: { icon: "🛢", sub: "锅炉房的管道迸裂，黑油四溢 · -1 燃料" },
          floodedFoodStores: { icon: "🌊", sub: "海水涌进储藏室，口粮泡烂了 · -1 食物" },
          fleeingPassengers: { icon: "🏃", sub: "惊恐的乘客冲上甲板 · 冒险放置 1 名乘客，再抽一张损伤卡" },
        };
        const n = kNon[ev.card] ?? { icon: "⚠", sub: d.text };
        out.push({ key: seq, icon: n.icon, title: `损伤：${d.name}`, sub: n.sub, tone: "danger" });
        break;
      }
      case "damage":
        out.push({ key: seq, icon: "💥", title: "船体受损", sub: `${uf.spaceName(ev.space)}遭到结构损伤`, tone: "danger" });
        break;
      case "monster":
        if ((ev.what === "hydra" || ev.what === "dagon") && ev.from === "deep" && ev.to !== "deep") {
          out.push({ key: seq, icon: ev.what === "hydra" ? "🐙" : "🔱", title: ev.what === "hydra" ? "母神海德拉浮出水面" : "父神大衮浮出水面", sub: `出现在${uf.spaceName(ev.to as uf.SpaceId)}`, tone: "dread" });
        }
        break;
      case "title": {
        const p = view.players[ev.seat];
        if (!p) break;
        const captain = ev.title === "captain";
        out.push({
          key: seq, icon: "", player: p, tone: "hope",
          title: `${p.name}${ev.seat === view.you ? "（你）" : ""} 成为${captain ? "船长" : "典籍守护者"}`,
          sub: `${ev.from !== null ? `${kTitleWhy[ev.why](name(ev.from))} · ` : ""}${captain ? "⚓ 驶向新航点时由船长选定航向" : "📖 行动时可查看并结算法术"}`,
        });
        break;
      }
      case "move": {
        // Being locked up matters on its own (and costs any title, announced right after).
        const p = view.players[ev.who];
        if (ev.to !== "brig" || !p) break;
        out.push({ key: seq, icon: "⛓", title: `${p.name}${ev.who === view.you ? "（你）" : ""} 被关进禁闭室`, sub: "被众人押走——在禁闭室里无法行动", tone: "danger" });
        break;
      }
      case "reveal":
        out.push({ key: seq, icon: "🩸", title: "叛徒现身", sub: `${name(ev.seat)} 公开了身份`, tone: "danger" });
        break;
      case "defeat":
        if (ev.target === "character") out.push({ key: seq, icon: "☠", title: `${name(ev.seat)} 倒下了`, sub: "被送往医务室", tone: "danger" });
        break;
    }
  }
  return out;
}

export function Announcer({ view, artOk, onBusy }: {
  view: uf.GameView;
  artOk: boolean;
  /** True while notices are queued or playing, so the table can hold back new windows. */
  onBusy?: (busy: boolean) => void;
}) {
  const seen = useRef<number>(view.events.at(-1)?.seq ?? 0);
  const last = useRef({ travel: view.travel, ritual: view.ritual });
  const [queue, setQueue] = useState<Notice[]>([]);
  useEffect(() => {
    const fresh = view.events.filter(e => e.seq > seen.current);
    if (fresh.length === 0) return;
    seen.current = fresh[fresh.length - 1].seq;
    const add = noticesFor(view, fresh, last.current).map(n => ({ ...n, key: ++noticeIds }));
    // Resync with the real tracks so faked (debug) events never hide a later real advance.
    last.current = { travel: view.travel, ritual: view.ritual };
    if (add.length) setQueue(q => [...q, ...add].slice(-6));
  }, [view.events]); // eslint-disable-line react-hooks/exhaustive-deps
  const cur = queue[0];
  // Report busy at once; report idle a moment after the queue drains (the next notice of the
  // same burst may still be on its way).
  const busy = queue.length > 0;
  useEffect(() => {
    if (busy) { onBusy?.(true); return; }
    const t = setTimeout(() => onBusy?.(false), 300);
    return () => clearTimeout(t);
  }, [busy]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!cur) return;
    const t = setTimeout(() => setQueue(q => q.slice(1)), cur.hold ?? kHold);
    return () => clearTimeout(t);
  }, [cur?.key]); // eslint-disable-line react-hooks/exhaustive-deps
  // No AnimatePresence here: its exit sometimes never completed, leaving an invisible
  // full-screen layer that swallowed every click. The whole life of a notice (in, hold, out)
  // is a CSS animation, and the element is removed when its timer ends.
  if (!cur) return null;
  const hold = cur.hold ?? kHold;
  return (
    <div key={cur.key} className={css.announce} data-tone={cur.tone} onClick={() => setQueue(q => q.slice(1))}
      style={{ ["--hold" as string]: `${hold}ms` }}>
      <div className={css.announceBarTop} />
      <div className={css.announceBarBottom} />
      <div className={css.announceBody}>
        {cur.player ? (
          <motion.div className={css.announcePortrait} initial={{ x: -260, rotate: -40, opacity: 0 }} animate={{ x: 0, rotate: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 140, damping: 14 }}
            style={{ borderColor: cur.player.revealed ? "#c94040" : kSeatColors[cur.player.seat % kSeatColors.length] }}>
            {artOk ? <img src={art.token(cur.player.character)} alt="" /> : <span>{uf.kCharacters[cur.player.character].name[0]}</span>}
          </motion.div>
        ) : (
          <div className={css.announceIcon}>{cur.icon}</div>
        )}
        <div className={css.announceTitle}>{cur.title}</div>
        <div className={css.announceSub}>{cur.sub}</div>
      </div>
    </div>
  );
}
